import { animate, style, transition, trigger } from '@angular/animations';
import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  HostListener,
  TemplateRef,
  computed,
  contentChild,
  effect,
  forwardRef,
  input,
  model,
  signal,
  untracked,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'ptl-custom-select',
  templateUrl: './custom-select.component.html',
  styleUrls: ['./custom-select.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomSelectComponent),
      multi: true,
    },
  ],
  animations: [
    trigger('dropdown', [
      transition(':enter', [
        style({ opacity: 0, transform: 'scaleY(0.9)' }),
        animate('120ms cubic-bezier(.33,1.25,.68,1)', style({ opacity: 1, transform: 'scaleY(1)' })),
      ]),
      transition(':leave', [animate('90ms cubic-bezier(.33,1.25,.68,1)', style({ opacity: 0, transform: 'scaleY(0.9)' }))]),
    ]),
  ],
})
export class CustomSelectComponent<T extends { uid: string; title: string }> implements ControlValueAccessor {
  options = input.required<T[]>();
  placeholder = input('Select...');
  disabled = model(false);
  multiple = input(false);
  maxSelection = input<number | null>(null); // null = unlimited

  optionTemplate = contentChild<TemplateRef<unknown>>('optionTemplate');
  dropdownList = viewChild<ElementRef<HTMLUListElement>>('dropdownList');
  searchInput = viewChild<ElementRef<HTMLInputElement>>('searchInput');

  isOpen = signal(false);
  highlightedIndex = signal(0);
  highlightedByMouse = signal(false);
  value = model<T[]>([]);

  searchQuery = signal('');
  searchTokens = computed(() => {
    return this.searchQuery()
      .toLowerCase()
      .split(' ')
      .filter((token) => token.length > 0);
  });
  availableOptionIndices = computed(() =>
    this.searchTokens().length === 0
      ? new Set([...this.options().keys()])
      : new Set(
          [...this.options().entries()]
            .filter(([_, option]) => this.searchTokens().every((token) => option.title.toLowerCase().includes(token)))
            .map(([idx, _]) => idx),
        ),
  );

  // eslint-disable-next-line @typescript-eslint/no-empty-function,no-empty-function
  private onChange = (_: T[]) => {};
  // eslint-disable-next-line @typescript-eslint/no-empty-function,no-empty-function
  private onTouched = () => {};

  constructor(private elRef: ElementRef) {
    effect(() => {
      if (!this.isOpen()) return;

      const highlightedIndex = this.highlightedIndex();
      if (this.highlightedByMouse() || !this.dropdownList() || !this.availableOptionIndices().has(highlightedIndex)) return;

      const list = this.dropdownList().nativeElement;
      const item = list.querySelector(`#option-${highlightedIndex}`) as HTMLElement;

      if (item) {
        setTimeout(() => item.scrollIntoView({ block: 'nearest' }), 0);
      }
    });

    effect(() => {
      if (this.availableOptionIndices().size > 0 && !this.availableOptionIndices().has(this.highlightedIndex())) {
        untracked(() => {
          this.highlightedIndex.set(Math.min(...this.availableOptionIndices()));
          this.highlightedByMouse.set(false);
        });
      }
    });

    effect(() => {
      const value = this.value();
      untracked(() => this.onChange([...value]));
    });
  }

  writeValue(value: T[]): void {
    this.value.set(value ?? []);
  }

  registerOnChange(fn: (value: T[]) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled.set(isDisabled);
  }

  isSelected(option: T) {
    return this.value().some((v) => this.compare(v, option));
  }

  private compare(option: T, value: T) {
    return option === value || (option && value && option.uid !== undefined && value.uid !== undefined && option.uid === value.uid);
  }

  toggleDropdown() {
    if (this.disabled()) {
      return;
    }
    if (this.isOpen()) {
      this.closeDropdown();
    } else {
      this.openDropdown();
    }
  }

  private openDropdown() {
    if (this.disabled()) return;
    this.highlightedByMouse.set(false);
    this.isOpen.set(true);
    setTimeout(() => {
      this.searchInput()?.nativeElement.focus();
    });
  }

  private closeDropdown() {
    this.isOpen.set(false);
    this.searchQuery.set('');
    this.onTouched();
  }

  selectOption(event: MouseEvent | KeyboardEvent, option: T) {
    event.stopPropagation();
    if (this.disabled()) return;
    if (this.multiple()) {
      this.toggleOption(option);
    } else {
      const newValue = [option];
      this.value.set(newValue);
      this.closeDropdown();
    }
  }

  toggleOption(option: T) {
    const idx = this.value().findIndex((v) => this.compare(v, option));
    if (idx > -1) {
      this.value.set(this.value().filter((_, i) => i !== idx));
    } else if (!this.maxSelection() || this.value().length < this.maxSelection()) {
      this.value.set([...this.value(), option]);
    }
  }

  @HostListener('keydown.arrowDown', ['$event'])
  onArrowDown(event: KeyboardEvent) {
    if (!this.isOpen()) this.openDropdown();
    else {
      this.highlightedIndex.update(this.shiftAndConstrainIndex(1));
      this.highlightedByMouse.set(false);
    }
    event.preventDefault();
  }

  @HostListener('keydown.arrowUp', ['$event'])
  onArrowUp(event: KeyboardEvent) {
    if (!this.isOpen()) this.openDropdown();
    else {
      this.highlightedIndex.update(this.shiftAndConstrainIndex(-1));
      this.highlightedByMouse.set(false);
    }
    event.preventDefault();
  }

  private shiftAndConstrainIndex(offset: number): (index: number) => number {
    return (index) => {
      if (this.availableOptionIndices().size === 0) return -1;

      const indexWithOffset = (((index + offset) % this.options().length) + this.options().length) % this.options().length;
      if (this.availableOptionIndices().has(indexWithOffset)) return indexWithOffset;

      const closestAvailableIndex = [...this.availableOptionIndices()].reduce(
        offset < 0
          ? (best, available) => (available > best && available < indexWithOffset ? available : best)
          : (best, available) => (available < best && available > indexWithOffset ? available : best),
        offset < 0 ? Number.MIN_SAFE_INTEGER : Number.MAX_SAFE_INTEGER,
      );

      if (closestAvailableIndex === Number.MIN_SAFE_INTEGER) {
        return Math.max(...this.availableOptionIndices());
      } else if (closestAvailableIndex === Number.MAX_SAFE_INTEGER) {
        return Math.min(...this.availableOptionIndices());
      } else {
        return closestAvailableIndex;
      }
    };
  }

  @HostListener('keydown.enter', ['$event'])
  onEnter(event: KeyboardEvent) {
    if (this.isOpen() && this.availableOptionIndices().size > 0) {
      this.selectOption(event, this.options()[this.highlightedIndex()]);
      event.preventDefault();
    }
  }

  @HostListener('keydown.escape', ['$event'])
  onEsc(event: KeyboardEvent) {
    this.closeDropdown();
    event.preventDefault();
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: MouseEvent) {
    if (!this.elRef.nativeElement.contains(event.target)) {
      this.closeDropdown();
    }
  }
}
